import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import useTrialConfig from '../hooks/useTrialConfig';
import logoImg from '../../images/logo-no-tagline.png';
import iconImg from '../../images/icon_transparent.png';

/**
 * PPL-36 — Email signup with Firebase email verification.
 *
 * Two states:
 *   1. The form (name / email / password) — submits via useAuth.signup, then
 *      triggers sendVerification() with a branded `actionCodeSettings.url`
 *      pointing at `/auth/action` on this same domain.
 *   2. The "check your email" confirmation, with a resend affordance.
 *
 * Continue URL: this page accepts `?continueUrl=/path` so callers (e.g. the
 * PPL-28 mediation-invite flow) can keep the user on track after verifying.
 *
 * Outside ProtectedRoute — mirrors the /login and /unsubscribe precedent.
 */

const Register = () => {
  const [mode, setMode] = useState('form'); // form | sent
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [resentNotice, setResentNotice] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { signup, sendVerification, user, error, loading, setError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ?continueUrl=/i/:token style routing for the mediation invite flow.
  // Only honour same-origin relative paths to avoid open-redirect risk.
  const searchParams = new URLSearchParams(location.search);
  const continueParam = searchParams.get('continueUrl');
  const isRelativeContinue = continueParam && continueParam.startsWith('/') && !continueParam.startsWith('//');
  const continueUrl = isRelativeContinue ? continueParam : null;

  // Once an already-verified user lands here, just bounce them into the app.
  useEffect(() => {
    if (user && user.emailVerified !== false) {
      navigate(continueUrl || '/f8chat', { replace: true });
    }
  }, [user, navigate, continueUrl]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validPassword = password.length >= 6;
  const valid = validEmail && validPassword;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError?.(null);
    if (!valid) return;
    setSubmitted(true);
    try {
      await signup(email, password, displayName.trim());
      // Fire-and-forward the verification email. If it fails we still proceed
      // to the "check your email" state — the user can hit resend.
      try {
        await sendVerification(continueUrl);
      } catch (verifyErr) {
        // Already surfaced via useAuth.error; user can resend.
        console.warn('Initial sendVerification failed:', verifyErr);
      }
      setMode('sent');
    } catch (err) {
      console.error('Signup error:', err);
    } finally {
      setSubmitted(false);
    }
  };

  const handleResend = async () => {
    setResentNotice('');
    setError?.(null);
    try {
      await sendVerification(continueUrl);
      setResentNotice('verification email re-sent. check your inbox.');
    } catch (err) {
      console.error('Resend verification error:', err);
    }
  };

  return (
    <div className="register-root" style={{
      position: 'fixed', inset: 0,
      background: '#0e0817',
      color: '#fff',
      fontFamily: 'inherit',
      textAlign: 'left',
      zIndex: 1000,
      WebkitOverflowScrolling: 'touch',
    }}>
      {/* LEFT — brand canvas (desktop only) */}
      <div className="register-brand" style={{
        position: 'relative', overflow: 'hidden',
        padding: '40px 56px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        background: 'linear-gradient(180deg, #1a0e22 0%, #0e0817 100%)',
      }}>
        <Aurora />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <img src={logoImg} alt="pplkpr" style={{ height: 40, width: 'auto', display: 'block' }} />
        </div>

        <div style={{ position: 'relative', maxWidth: 540 }}>
          <BigOrb />
          <h1 style={{
            margin: 0, position: 'relative', zIndex: 2,
            fontSize: 'clamp(48px, 5.5vw, 80px)', lineHeight: 0.96,
            letterSpacing: '-0.045em', fontWeight: 800,
          }}>
            join a kinder<br />
            corner of the{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f0abfc, #d946ef 40%, #a855f7)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              color: 'transparent',
            }}>internet.</span>
          </h1>
          <p style={{
            margin: '24px 0 0', maxWidth: 460, position: 'relative', zIndex: 2,
            color: 'rgba(232, 220, 250, 0.78)', fontSize: 17, lineHeight: 1.55,
          }}>
            real people. small circles. an ai friend named f8.
            sign up free — we&apos;ll send a quick confirmation email.
          </p>
        </div>

        <div style={{ position: 'relative', color: 'rgba(220,200,240,0.5)', fontSize: 12 }}>
          better relationships. period.
        </div>
      </div>

      {/* RIGHT — auth panel */}
      <div className="register-auth" style={{
        position: 'relative', overflow: 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background:
          'radial-gradient(700px 400px at 50% 30%, rgba(124, 58, 237, 0.15), transparent 60%), ' +
          'rgba(14, 8, 23, 0.6)',
        backdropFilter: 'blur(8px)',
      }}>
        <div className="register-auth-inner" style={{ width: '100%', maxWidth: 380 }}>
          <div className="register-mobile-logo">
            <img src={logoImg} alt="pplkpr" />
          </div>

          {mode === 'form' && (
            <SignupForm
              displayName={displayName}
              setDisplayName={setDisplayName}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPw={showPw}
              setShowPw={setShowPw}
              valid={valid}
              loading={loading || submitted}
              error={error}
              onSubmit={handleSubmit}
            />
          )}

          {mode === 'sent' && (
            <CheckYourEmail
              email={email}
              onResend={handleResend}
              resentNotice={resentNotice}
              error={error}
              loading={loading}
            />
          )}

          <div style={{
            marginTop: 32, paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            color: 'rgba(220,200,240,0.45)', fontSize: 11.5,
          }}>
            <span>© pplkpr 2026</span>
            <div style={{ display: 'flex', gap: 16 }}>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>privacy</a>
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>terms</a>
              <Link to="/faq" style={{ color: 'inherit', textDecoration: 'none' }}>help</Link>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes registerFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes registerOrbDrift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -20px) scale(1.05); }
        }
        @keyframes registerOrbDrift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 30px) scale(0.95); }
        }
        .register-root { display: block; height: 100vh; height: 100dvh; overflow: auto; }
        .register-brand { display: none; }
        .register-auth { min-height: 100%; padding: 28px 20px calc(28px + env(safe-area-inset-bottom)); }
        .register-auth-inner { max-width: 380px; margin: 0 auto; }
        .register-mobile-logo { display: flex; justify-content: center; margin: 4px 0 24px; }
        .register-mobile-logo img { height: 32px; width: auto; display: block; }
        .register-icon-img { width: 56px; height: 56px; }
        .register-h2 { font-size: 26px; }
        @media (min-width: 600px) {
          .register-auth { padding: 40px 32px; }
          .register-icon-img { width: 64px; height: 64px; }
          .register-h2 { font-size: 30px; }
        }
        @media (min-width: 900px) {
          .register-root {
            display: grid;
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
            height: 100vh; height: 100dvh;
            overflow: hidden;
          }
          .register-brand { display: flex; }
          .register-auth { border-left: 1px solid rgba(255,255,255,0.04); padding: 40px 32px; }
          .register-mobile-logo { display: none; }
        }
      `}</style>
    </div>
  );
};

// ─── Signup form ────────────────────────────────────────────
function SignupForm({
  displayName, setDisplayName,
  email, setEmail,
  password, setPassword,
  showPw, setShowPw,
  valid, loading, error,
  onSubmit,
}) {
  // PPL-527 — null unless the served config says the trial is actually on; the
  // one line under the CTA is the only thing that reads it. The hook lives here
  // rather than in Register because this is the component that owns the CTA,
  // and Register already passes eleven props down.
  const trialConfig = useTrialConfig();

  return (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: 24 }}>
        <img src={iconImg} alt="" className="register-icon-img" style={{ display: 'block', marginBottom: 14 }} />
        <div style={{ fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>create your account</div>
      </div>

      <h2 className="register-h2" style={{ margin: 0, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
        sign up
      </h2>
      <p style={{ margin: '6px 0 28px', color: 'rgba(220,200,240,0.55)', fontSize: 13.5, lineHeight: 1.5 }}>
        we&apos;ll send a quick confirmation email to verify your address.
      </p>

      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={{
          fontSize: 11, color: 'rgba(220,200,240,0.55)', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
        }}>name <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: 'rgba(220,200,240,0.4)' }}>(optional)</span></div>
        <input
          autoFocus
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="what should we call you?"
          autoComplete="name"
          style={inputStyle}
          onFocus={focusStyle}
          onBlur={blurStyle}
        />
      </label>

      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={labelStyle}>email</div>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          style={inputStyle}
          onFocus={focusStyle}
          onBlur={blurStyle}
        />
      </label>

      <label style={{ display: 'block' }}>
        <div style={labelStyle}>password</div>
        <div style={{ position: 'relative' }}>
          <input
            type={showPw ? 'text' : 'password'}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="at least 6 characters"
            autoComplete="new-password"
            style={{ ...inputStyle, padding: '14px 44px 14px 16px' }}
            onFocus={focusStyle}
            onBlur={blurStyle}
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            aria-label={showPw ? 'hide password' : 'show password'}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer',
              padding: 8, color: 'rgba(220,200,240,0.5)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {showPw ? <EyeOnIcon /> : <EyeOffIcon />}
          </button>
        </div>
      </label>

      {error && (
        <div style={errorBox}>{error}</div>
      )}

      <button
        type="submit"
        disabled={!valid || loading}
        style={{
          appearance: 'none', border: 0, cursor: (valid && !loading) ? 'pointer' : 'default',
          width: '100%', marginTop: 24,
          padding: '14px 16px', borderRadius: 14,
          background: valid ? 'linear-gradient(135deg, #d946ef 0%, #8b5cf6 100%)' : 'rgba(255,255,255,0.06)',
          color: '#fff', fontSize: 14.5, fontWeight: 700, fontFamily: 'inherit',
          boxShadow: valid ? '0 12px 32px rgba(217, 70, 239, 0.4)' : 'none',
          opacity: (valid && !loading) ? 1 : 0.5,
          transition: 'all 0.2s',
        }}
      >
        {loading ? 'creating account…' : 'create account →'}
      </button>

      {/* PPL-527 — the one thing a stranger should know before signing up: this
          account starts on premium. Served, never asserted: `trialDays` comes
          off global_settings/trialConfig and the line renders only while that
          config says the trial is on, so the page can never promise something
          signup will not grant. Nothing equivalent on /login — it is not news
          to someone who already has an account. */}
      {trialConfig && (
        <div style={{
          marginTop: 12, fontSize: 12.5, color: 'rgba(220,200,240,0.55)',
          lineHeight: 1.5, textAlign: 'center',
        }}>
          {/* `trialDays` can legitimately be 1 (the server's MIN_TRIAL_DAYS), and
              "1 days" is the kind of sentence that makes a stranger trust the
              rest of the page less. Same agreement the app's line carries. */}
          {`every new account starts with ${trialConfig.trialDays} ${
            trialConfig.trialDays === 1 ? 'day' : 'days'
          } of full premium. no card.`}
        </div>
      )}

      <div style={{
        marginTop: 22, fontSize: 12.5, color: 'rgba(220,200,240,0.55)', lineHeight: 1.5,
      }}>
        already have an account?{' '}
        <Link to="/login" style={{
          color: '#a78bfa', fontWeight: 600, textDecoration: 'none',
        }}>log in →</Link>
      </div>

      <div style={{ marginTop: 22, fontSize: 11, color: 'rgba(220,200,240,0.45)', lineHeight: 1.5 }}>
        by signing up you agree to our <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>terms</a> &amp; <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa' }}>privacy notice</a>.
      </div>
    </form>
  );
}

// ─── Check-your-email confirmation ──────────────────────────
function CheckYourEmail({ email, onResend, resentNotice, error, loading }) {
  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 14,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(217, 70, 239, 0.18), rgba(124, 58, 237, 0.18))',
          border: '1px solid rgba(217, 70, 239, 0.35)',
          marginBottom: 14,
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="#f0abfc" strokeWidth="1.7" />
            <path d="m4 7 8 6 8-6" stroke="#f0abfc" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>welcome to pplkpr</div>
      </div>

      <h2 className="register-h2" style={{ margin: 0, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.1 }}>
        check your email
      </h2>
      <p style={{ margin: '6px 0 28px', color: 'rgba(220,200,240,0.65)', fontSize: 14, lineHeight: 1.55 }}>
        we sent a verification link to{' '}
        <span style={{ color: '#fff', fontWeight: 600 }}>{email}</span>.{' '}
        tap it to confirm your address — then come back here and you&apos;ll be in.
      </p>

      {resentNotice && (
        <div style={{
          padding: '10px 12px', borderRadius: 10, marginBottom: 16,
          background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.25)',
          color: '#86efac', fontSize: 12.5, lineHeight: 1.4,
        }}>
          {resentNotice}
        </div>
      )}

      {error && (
        <div style={errorBox}>{error}</div>
      )}

      <button
        type="button"
        onClick={onResend}
        disabled={loading}
        style={{
          appearance: 'none', cursor: loading ? 'default' : 'pointer',
          width: '100%', padding: '13px 16px', borderRadius: 14,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? 'sending…' : 'resend verification email'}
      </button>

      <div style={{
        marginTop: 22, fontSize: 12.5, color: 'rgba(220,200,240,0.55)', lineHeight: 1.5,
      }}>
        already verified?{' '}
        <Link to="/login" style={{
          color: '#a78bfa', fontWeight: 600, textDecoration: 'none',
        }}>log in →</Link>
      </div>

      <div style={{ marginTop: 18, fontSize: 11.5, color: 'rgba(220,200,240,0.45)', lineHeight: 1.55 }}>
        didn&apos;t get the email? check your spam folder. if it&apos;s really not arriving, ping{' '}
        <a href="mailto:support@pplkpr.ai" style={{ color: '#a78bfa' }}>support@pplkpr.ai</a>.
      </div>
    </>
  );
}

// ─── Shared styles ──────────────────────────────────────────
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '14px 16px', borderRadius: 12,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: '#fff', fontSize: 14.5, fontFamily: 'inherit', outline: 'none',
  transition: 'all 0.15s',
};

const labelStyle = {
  fontSize: 11, color: 'rgba(220,200,240,0.55)', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
};

const errorBox = {
  marginTop: 16, padding: '10px 12px', borderRadius: 10,
  background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)',
  color: '#fca5a5', fontSize: 12.5, lineHeight: 1.4,
};

const focusStyle = (e) => {
  e.target.style.borderColor = 'rgba(217, 70, 239, 0.5)';
  e.target.style.background = 'rgba(217, 70, 239, 0.06)';
};
const blurStyle = (e) => {
  e.target.style.borderColor = 'rgba(255,255,255,0.1)';
  e.target.style.background = 'rgba(255,255,255,0.04)';
};

// ─── Icons ──────────────────────────────────────────────────
function EyeOnIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M3 3l18 18M10.5 6.3A10 10 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-2.6 3.5M6.5 8.5A14 14 0 0 0 2 12s3.5 6 10 6c1.6 0 3-.4 4.3-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M9.5 9.5a3 3 0 0 0 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function BigOrb() {
  return (
    <div style={{
      position: 'absolute',
      top: '-30%', right: '-10%',
      width: 460, height: 460,
      pointerEvents: 'none', zIndex: 1,
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, #f0abfc, #d946ef 35%, #7c3aed 70%, transparent 75%)',
        filter: 'blur(20px)',
        opacity: 0.55,
        animation: 'registerOrbDrift 12s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', inset: '15%', borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, #f0abfc, #d946ef 50%, #7c3aed)',
        boxShadow: '0 0 100px rgba(217, 70, 239, 0.55)',
        opacity: 0.85,
      }} />
    </div>
  );
}

function Aurora() {
  return (
    <>
      <div style={{
        position: 'absolute', top: '40%', left: '-15%',
        width: 480, height: 480, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124, 58, 237, 0.25), transparent 70%)',
        filter: 'blur(40px)',
        animation: 'registerOrbDrift2 18s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-10%', right: '20%',
        width: 380, height: 380, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.15), transparent 70%)',
        filter: 'blur(40px)',
        animation: 'registerOrbDrift 22s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.4,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '3px 3px',
        mixBlendMode: 'overlay',
      }} />
    </>
  );
}

export default Register;
